import {EditableField} from "./external-forecast.edit";
import {
  CellKey, ExternalForecastAverage, ExternalForecastCt, ExternalForecastPeriod, ExternalForecastYear
} from "./external-forecast.model";

/** Number of leading rows fixed above the scrolling ones (Contacts Received and its % Distribution). */
export const PINNED_ROW_COUNT = 2;
export const CT_DISTRIBUTION_GROUP = "ctDistribution";
export const NEW_CT_CONTACTS_GROUP = "newCtContacts";

export interface ComparisonRow {
  label: string;
  /** Keyed by period.period; null is a blank cell. */
  values: Record<string, number | null>;
  total: number | null;
  average: number | null;
  format: "count" | "percent";
  /** Drives the row colour (white vs grey) like Plan Detail. */
  editable: boolean;
  /** Emphasised calculated rows: bold, dark text like the period headers. */
  bold: boolean;
  /** Parameter label rendered bold in dark text (all bold rows plus the contacts-received and first % Distribution rows). */
  labelBold: boolean;
  /** First row of a major metric group: drawn with a thick divider above it. */
  groupStart: boolean;
  /** Contacts Received, the two % Distribution rows, Total Demand and Contact Center Demand: value cells shown in blue like Plan Detail's calculated cells. */
  calculated: boolean;
  /** Set on an expandable group header row; its children carry the same value in parentKey. */
  groupKey?: string;
  parentKey?: string;
  /** Set on the five input rows (Business Metric, Contact Rate, %CC, AI Mix, AI Volume). */
  field?: EditableField;
  /** Set on every parameter row; cells look their meta up in the response by it. */
  cellKey?: CellKey;
  /** Set on CT Distribution and New CT Contacts children. */
  ctOid?: string;
  /** True on CT Distribution children (the editable CT shares). */
  ctShare?: boolean;
}

type PeriodValueField = {
  [K in keyof ExternalForecastPeriod]: ExternalForecastPeriod[K] extends number | null ? K : never
}[keyof ExternalForecastPeriod];

interface ParameterRowDef {
  labelKey: string;
  field: PeriodValueField;
  cellKey: CellKey;
  editField?: EditableField;
  format: "count" | "percent";
  /** Field of year.average shown in the Average column; null leaves it blank. */
  averageField: keyof ExternalForecastAverage | null;
  editable: boolean;
  bold: boolean;
  labelBold?: boolean;
  groupStart?: boolean;
  calculated?: boolean;
}

/** The 13 parameter rows, in display order. */
const PARAMETER_ROWS: ParameterRowDef[] = [
  {labelKey: "external.forecast.row.contacts.received", field: "contactsReceived", cellKey: "CONTACTS_RECEIVED", format: "count",
    averageField: "contactsReceived", editable: false, bold: false, labelBold: true, calculated: true},
  {labelKey: "external.forecast.param.pct.distribution", field: "contactsReceivedPct", cellKey: "CONTACTS_RECEIVED_PCT", format: "percent",
    averageField: null, editable: false, bold: false, labelBold: true, calculated: true},
  {labelKey: "external.forecast.param.business.metric", field: "businessMetric", cellKey: "BUSINESS_METRIC", editField: "businessMetric", format: "count",
    averageField: "businessMetric", editable: true, bold: false},
  {labelKey: "external.forecast.param.pct.distribution", field: "businessMetricPct", cellKey: "BUSINESS_METRIC_PCT", format: "percent",
    averageField: null, editable: false, bold: false, calculated: true},
  {labelKey: "external.forecast.param.contact.rate", field: "contactRate", cellKey: "CONTACT_RATE", editField: "contactRate", format: "percent",
    averageField: null, editable: true, bold: false},
  {labelKey: "external.forecast.param.total.volume", field: "totalVolume", cellKey: "TOTAL_VOLUME", format: "count",
    averageField: "totalVolume", editable: false, bold: true, groupStart: true, calculated: true},
  {labelKey: "external.forecast.param.pct.call.center.volume", field: "ccVolumePct", cellKey: "CC_VOLUME_PCT", editField: "ccVolumePct", format: "percent",
    averageField: null, editable: true, bold: false},
  {labelKey: "external.forecast.param.call.center.volume", field: "ccVolume", cellKey: "CC_VOLUME", format: "count",
    averageField: "ccVolume", editable: false, bold: true, groupStart: true, calculated: true},
  {labelKey: "external.forecast.param.ai.mix", field: "aiMix", cellKey: "AI_MIX", editField: "aiMix", format: "percent",
    averageField: null, editable: true, bold: false},
  {labelKey: "external.forecast.param.ai.volume", field: "aiVolume", cellKey: "AI_VOLUME", editField: "aiVolume", format: "count",
    averageField: "aiVolume", editable: true, bold: false},
  {labelKey: "external.forecast.param.human.volume", field: "humanVolume", cellKey: "HUMAN_VOLUME", format: "count",
    averageField: "humanVolume", editable: false, bold: false, groupStart: true},
  {labelKey: "external.forecast.param.new.contacts.received", field: "newContactsReceived", cellKey: "NEW_CONTACTS_RECEIVED", format: "count",
    averageField: "newContactsReceived", editable: false, bold: true, groupStart: true},
  {labelKey: "external.forecast.param.pct.distribution", field: "newContactsReceivedPct", cellKey: "NEW_CONTACTS_RECEIVED_PCT", format: "percent",
    averageField: null, editable: false, bold: true}
];

/** CT row label like the rest of the app: "<id> <name>", name only on CXone tenants or when the CT has no id. */
type CtInfoList = {oid: string; id: string | number | null}[] | null | undefined;

/** The CT's integral ID shown in labels and used for ordering; undefined on CXone tenants or when it has none. */
function ctIdOf(ctOid: string, ctInfo: CtInfoList, isCXOne: boolean): string | number | undefined {
  const id = isCXOne ? null : ctInfo?.find(c => c.oid === ctOid)?.id;
  return id != null && id !== "" ? id : undefined;
}

export function ctLabel(ctOid: string, ctName: string, ctInfo: CtInfoList, isCXOne: boolean): string {
  const id = ctIdOf(ctOid, ctInfo, isCXOne);
  return id !== undefined ? `${id} ${ctName}` : ctName;
}

/** CT order like Plan Detail's sortMUs: CTs with an ID first by numeric ID, then by name; CXone sorts by name only. */
export function ctComparator(ctInfo: CtInfoList, isCXOne: boolean): (a: ExternalForecastCt, b: ExternalForecastCt) => number {
  return (a, b) => {
    const idA = ctIdOf(a.ctOid, ctInfo, isCXOne);
    const idB = ctIdOf(b.ctOid, ctInfo, isCXOne);
    if (idA !== undefined && idB === undefined) {
      return -1;
    }
    if (idA === undefined && idB !== undefined) {
      return 1;
    }
    if (idA !== undefined && idB !== undefined && Number(idA) !== Number(idB)) {
      return Number(idA) - Number(idB);
    }
    return a.ctName.localeCompare(b.ctName, undefined, {sensitivity: "base"});
  };
}

function row(label: string, partial: Partial<ComparisonRow>): ComparisonRow {
  return {
    label, values: {}, total: null, average: null, format: "percent", editable: false, bold: false,
    labelBold: false, groupStart: false, calculated: false, ...partial
  };
}

/**
 * Grid rows for one year of the Retrieve response. Totals and averages are read from the response, never computed;
 * a null stays null (blank cell).
 */
export function buildRows(year: ExternalForecastYear, label: (key: string) => string,
                          ctLabelOf: (ct: ExternalForecastCt) => string = ct => ct.ctName,
                          ctCompare?: (a: ExternalForecastCt, b: ExternalForecastCt) => number): ComparisonRow[] {
  const valuesOf = (read: (p: ExternalForecastPeriod) => number | null) =>
    Object.fromEntries(year.periods.map(p => [p.period, read(p)]));
  const averageValue = (field: keyof ExternalForecastAverage | null): number | null =>
    field ? year.average[field] as number | null : null;

  const parameterRows = PARAMETER_ROWS.map(def => row(label(def.labelKey), {
    values: valuesOf(p => p[def.field]),
    total: year.annual[def.field],
    average: averageValue(def.averageField),
    format: def.format,
    editable: def.editable,
    field: def.editField,
    cellKey: def.cellKey,
    bold: def.bold,
    labelBold: def.bold || !!def.labelBold,
    groupStart: !!def.groupStart,
    calculated: !!def.calculated
  }));

  const periodCts = year.periods[0]?.cts ?? [];
  const cts = ctCompare ? [...periodCts].sort(ctCompare) : periodCts;
  const annualCt = (ctOid: string) => year.annual.cts.find(c => c.ctOid === ctOid);
  const averageCt = (ctOid: string) => year.average.cts.find(c => c.ctOid === ctOid);
  const ctValues = (ctOid: string, read: (ct: ExternalForecastPeriod["cts"][number]) => number | null) =>
    valuesOf(p => {
      const ct = p.cts.find(c => c.ctOid === ctOid);
      return ct ? read(ct) : null;
    });

  return [
    ...parameterRows,
    row(label("external.forecast.param.ct.distribution"),
      {bold: true, labelBold: true, groupStart: true, groupKey: CT_DISTRIBUTION_GROUP}),
    ...cts.map(ct => row(ctLabelOf(ct), {
      values: ctValues(ct.ctOid, c => c.pct),
      total: annualCt(ct.ctOid)?.pct ?? null,
      editable: true,
      ctOid: ct.ctOid,
      ctShare: true,
      parentKey: CT_DISTRIBUTION_GROUP
    })),
    row(label("external.forecast.param.new.ct.contacts.received"),
      {bold: true, labelBold: true, groupStart: true, groupKey: NEW_CT_CONTACTS_GROUP}),
    ...cts.map(ct => row(ctLabelOf(ct), {
      values: ctValues(ct.ctOid, c => c.volume),
      total: annualCt(ct.ctOid)?.volume ?? null,
      average: averageCt(ct.ctOid)?.volume ?? null,
      format: "count",
      ctOid: ct.ctOid,
      parentKey: NEW_CT_CONTACTS_GROUP
    }))
  ];
}
